import React, { useState, useEffect } from 'react';
import { getData, postData, putData, deleteData, API_BASE_URL, API_TOKEN } from '../helpers/ApiSettings';
import FormulaTable from '../components/Formulas/FormulaTable';
import AddFormulaModal from '../components/Formulas/AddFormulaModal';
import EditFormulaModal from '../components/Formulas/EditFormulaModal';

const Formulas = () => {
  const [formulas, setFormulas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingFormula, setEditingFormula] = useState(null);
  
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [pageSize] = useState(8); // Items per page

  const [newFormula, setNewFormula] = useState({
    name: '',
    article_quantity: 1,
    available: true,
    components_pricing_formula_prices: []
  });

  const fetchFormulas = async (page = currentPage) => {
    try {
      setLoading(true);
      // Add pagination parameters to the API call
      const response = await getData(`/formulas?populate=*&pagination[page]=${page}&pagination[pageSize]=${pageSize}`);
      setFormulas(response.data.data);
      // Calculate total pages from meta data
      const total = response.data.meta.pagination.total;
      setTotalPages(Math.ceil(total / pageSize));
      setError(null);
    } catch (err) {
      setError('Erreur lors du chargement des formules');
      console.error('Error fetching formulas:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchFormulas();
  }, [currentPage]); // Re-fetch when page changes

  const handlePageChange = (page) => {
    setCurrentPage(page);
    window.scrollTo(0, 0); // Scroll to top when page changes
  };

  // Pagination component
  const Pagination = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    
    // Adjust start page if we're near the end
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    // Previous button
    pages.push(
      <li key="prev" className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &laquo;
        </button>
      </li>
    );

    // First page if not visible
    if (startPage > 1) {
      pages.push(
        <li key={1} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(1)}>1</button>
        </li>
      );
      if (startPage > 2) {
        pages.push(<li key="ellipsis1" className="page-item disabled"><span className="page-link">...</span></li>);
      }
    }

    // Page numbers
    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <li key={i} className={`page-item ${currentPage === i ? 'active' : ''}`}>
          <button className="page-link" onClick={() => handlePageChange(i)}>{i}</button>
        </li>
      );
    }

    // Last page if not visible
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        pages.push(<li key="ellipsis2" className="page-item disabled"><span className="page-link">...</span></li>);
      }
      pages.push(
        <li key={totalPages} className="page-item">
          <button className="page-link" onClick={() => handlePageChange(totalPages)}>{totalPages}</button>
        </li>
      );
    }

    // Next button
    pages.push(
      <li key="next" className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
        <button
          className="page-link"
          onClick={() => handlePageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          &raquo;
        </button>
      </li>
    );

    return (
      <nav aria-label="Formula navigation" className="mt-4">
        <ul className="pagination justify-content-center">
          {pages}
        </ul>
      </nav>
    );
  };

  const handleAddFormula = async (e, formulaData) => {
    e.preventDefault();
    try {
      const { file, ...formulaDetails } = formulaData;
      const response = await postData('/formulas', {
        data: formulaDetails
      });

      const newFormulaId = response.data.data.id;

      if (file) {
        await handleImageUpload(newFormulaId, file);
      }

      await fetchFormulas(currentPage);
      setNewFormula({
        name: '',
        article_quantity: 1,
        available: true,
        components_pricing_formula_prices: []
      });

      window.$('#addFormulaModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      alert('Formule ajoutée avec succès');
    } catch (err) {
      alert('Erreur lors de l\'ajout de la formule');
      console.error('Error adding formula:', err);
    }
  };

  const handleUpdateFormula = async (e) => {
    e.preventDefault();
    try {
      const cleanPrices = editingFormula.components_pricing_formula_prices.map(({ id: priceId, ...priceData }) => ({
        duration_days: priceData.duration_days,
        price: priceData.price,
        deposit: priceData.deposit
      }));

      const updateData = {
        name: editingFormula.name,
        slug: editingFormula.slug,
        description: editingFormula.description,
        article_quantity: editingFormula.article_quantity,
        available: editingFormula.available,
        components_pricing_formula_prices: cleanPrices
      };

      await putData(`/formulas/${editingFormula.documentId}`, {
        data: updateData
      });

      if (editingFormula.newImage) {
        await handleImageUpload(editingFormula.id, editingFormula.newImage);
      }

      await fetchFormulas(currentPage);
      window.$('#editFormulaModal').modal('hide');
      document.querySelector('.modal-backdrop')?.remove();
      setEditingFormula(null);
      alert('Formule mise à jour avec succès');
    } catch (err) {
      console.error('Error updating formula:', err.response?.data || err);
      alert('Erreur lors de la mise à jour de la formule: ' + (err.response?.data?.error?.message || err.message));
    }
  };

  const handleDeleteFormula = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer cette formule ?')) {
      try {
        await deleteData(`/formulas/${id}`);
        await fetchFormulas(currentPage);
        alert('Formule supprimée avec succès');
      } catch (err) {
        alert('Erreur lors de la suppression de la formule');
        console.error('Error deleting formula:', err);
      }
    }
  };

  const handleEditClick = (formula) => {
    const formulaCopy = JSON.parse(JSON.stringify(formula));
    setEditingFormula(formulaCopy);
    window.$('#editFormulaModal').modal('show');
  };

  const handleImageUpload = async (formulaId, file) => {
    try {
      const formData = new FormData();
      formData.append('ref', 'api::formula.formula');
      formData.append('refId', formulaId);
      formData.append('field', 'image');
      formData.append('files', file);

      const response = await fetch(`${API_BASE_URL}/api/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${API_TOKEN}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error?.message || 'Upload failed');
      }

      await fetchFormulas(currentPage);
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Erreur lors du téléchargement de l\'image');
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="text-center p-5">
        <div className="spinner-border" role="status">
          <span className="visually-hidden">Chargement...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        {error}
      </div>
    );
  }

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h2 className="h4 mb-0">Formules</h2>
        <button 
          className="btn btn-primary"
          onClick={() => window.$('#addFormulaModal').modal('show')}
        >
          Ajouter une formule
        </button>
      </div>

      <div className="card">
        <div className="card-body">
          {formulas.length === 0 ? (
            <p className="text-center text-muted my-5">Aucune formule pour le moment</p>
          ) : (
            <>
              <FormulaTable 
                formulas={formulas}
                onEdit={handleEditClick}
                onDelete={handleDeleteFormula}
              />
              <Pagination />
            </>
          )}
        </div>
      </div>

      <AddFormulaModal
        newFormula={newFormula}
        setNewFormula={setNewFormula}
        onSubmit={handleAddFormula}
      />
      
      <EditFormulaModal
        editingFormula={editingFormula}
        setEditingFormula={setEditingFormula}
        onSubmit={handleUpdateFormula}
      />
    </div>
  );
};

export default Formulas;